/**
 * LoginPage — a redirect to the realm, plus the states only the app can show.
 *
 * States rendered, in priority order:
 *   1. Build has no realm configured   → configuration banner.
 *   2. No live session                 → "Taking you to sign in…" and the
 *      effect sends the browser to the realm; the form lives there.
 *   3. /me still resolving or renewing → "Signing you in…" spinner.
 *   4. The API refused the sent token  → error banner with its reason and
 *      sign-out.
 *   4a. /me failed otherwise           → "Try again"; never a redirect, which
 *      the realm would bounce straight back into the same failure.
 *   5. Signed in, not staff            → banner and sign-out.
 *   6. Signed in as staff              → the effect navigates away.
 *
 * Every state follows the live session through the `me` query, so a token
 * that renews or runs out moves the page on; a token that was never sent
 * is a trip to the realm, not a refusal by the API.
 */
// Not one of the four page shapes: the sign-in page renders outside the shell, before
// there is a rail, a panel, or a measure to keep (Feature 70, §6.1).
import { useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';

import { useAuth } from '../../auth/AuthContext.js';
import { HOME_PATH, isOidcConfigured, signIn, signOut } from '../../auth/oidc.js';
import { Button } from '../../ui/Button.js';
import { Spinner } from '../../ui/Spinner.js';
import { AuthFrame } from './AuthFrame.js';

interface LocationState {
  returnTo?: string;
}

/** The sign-in route: where the guard sends anyone without a staff principal. */
export function LoginPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const returnTo = (location.state as LocationState | null)?.returnTo ?? HOME_PATH;
  const { user: principal, isInternal, refusal, isError, isLoading, isFetching, retry } = useAuth();
  // One redirect per mount: strict mode runs effects twice, and a re-render
  // while the browser is already leaving must not start a second one.
  const redirected = useRef(false);

  const configured = isOidcConfigured();
  // /me has answered for the session as it is now, renewal included.
  const settled = !isLoading && !isFetching;
  const staff = principal !== null && isInternal;
  // Nothing was sent, so there is nothing the API refused: the realm is next.
  const noSession = configured && settled && !isError && principal === null && refusal === null;
  const unreachable = configured && settled && isError && principal === null;
  const refused = configured && settled && principal === null && refusal !== null;
  const notStaff = configured && settled && principal !== null && !isInternal;
  const showRejection = refused || notStaff;
  const showSpinner = configured && !noSession && !unreachable && !showRejection;

  useEffect(() => {
    if (staff) navigate(returnTo, { replace: true });
  }, [staff, navigate, returnTo]);

  useEffect(() => {
    if (!noSession || redirected.current) return;
    redirected.current = true;
    void signIn(returnTo);
  }, [noSession, returnTo]);

  return (
    <AuthFrame>
      {!configured && (
        <div
          role="alert"
          style={{
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-card)',
            padding: 16,
            fontSize: 'var(--text-body-md)',
            color: 'var(--danger)',
          }}
        >
          This build has no identity realm configured. Set VITE_OIDC_AUTHORITY and
          VITE_OIDC_CLIENT_ID and rebuild.
        </div>
      )}

      {noSession && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 11,
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-card)',
            padding: 30,
          }}
        >
          <Spinner size={28} />
          <div style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
            Taking you to sign in…
          </div>
        </div>
      )}

      {showSpinner && (
        <div
          role="status"
          aria-live="polite"
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 11,
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-card)',
            padding: 30,
          }}
        >
          <Spinner size={28} />
          <div style={{ fontSize: 'var(--text-body-md)', color: 'var(--text-muted)' }}>
            Signing you in…
          </div>
        </div>
      )}

      {unreachable && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 11,
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-card)',
            padding: 16,
          }}
        >
          <div
            role="alert"
            style={{
              fontSize: 'var(--text-body-md)',
              color: 'var(--danger)',
            }}
          >
            Could not reach the API to load your account. Try again in a moment.
          </div>
          <Button variant="secondary" onClick={retry}>
            Try again
          </Button>
        </div>
      )}

      {showRejection && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            gap: 11,
            borderRadius: 'var(--radius-input)',
            background: 'var(--surface-card)',
            padding: 16,
          }}
        >
          <div
            role="alert"
            style={{
              borderRadius: 'var(--radius-chip)',
              border: '1px solid var(--danger-line)',
              background: 'var(--danger-tint)',
              paddingLeft: 11,
              paddingRight: 11,
              paddingTop: 7,
              paddingBottom: 7,
              fontSize: 'var(--text-body-md)',
              color: 'var(--danger)',
            }}
          >
            {notStaff ? (
              <>
                Signed in as <strong>{principal?.email}</strong>, but this account is not registered
                as Optum platform staff. Ask a platform admin for an invitation, or sign out.
              </>
            ) : (
              <>
                The API refused your sign-in. Check that the API verifies the same realm this app
                was built for.
                <span
                  className="mono"
                  style={{ display: 'block', marginTop: 5, fontSize: 'var(--text-body-sm)' }}
                >
                  {refusal}
                </span>
              </>
            )}
          </div>
          <Button variant="secondary" onClick={() => void signOut()}>
            Sign out
          </Button>
        </div>
      )}
    </AuthFrame>
  );
}
